"use client";

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Search, 
  MapPin, 
  Building2, 
  Home, 
  Filter, 
  ChevronDown, 
  X,
  RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

// Define the type for filter options
type FilterOption = {
  id: string;
  name: string;
};

// Nigerian states and cities data
const nigerianStates: FilterOption[] = [
  { id: 'lagos', name: 'Lagos' },
  { id: 'abuja', name: 'Abuja' },
  { id: 'rivers', name: 'Rivers' },
  { id: 'oyo', name: 'Oyo' },
  { id: 'kano', name: 'Kano' },
  { id: 'enugu', name: 'Enugu' },
  { id: 'kaduna', name: 'Kaduna' },
  { id: 'anambra', name: 'Anambra' },
  // Add more states as needed
];

// Sample city data - in production, this would be dynamically loaded based on selected state
const citiesByState: Record<string, FilterOption[]> = {
  lagos: [
    { id: 'ikeja', name: 'Ikeja' },
    { id: 'lekki', name: 'Lekki' },
    { id: 'victoria-island', name: 'Victoria Island' },
    { id: 'ikoyi', name: 'Ikoyi' },
    { id: 'ajah', name: 'Ajah' },
    { id: 'yaba', name: 'Yaba' },
    { id: 'surulere', name: 'Surulere' },
  ],
  abuja: [
    { id: 'maitama', name: 'Maitama' },
    { id: 'asokoro', name: 'Asokoro' },
    { id: 'wuse', name: 'Wuse' },
    { id: 'garki', name: 'Garki' },
    { id: 'jabi', name: 'Jabi' },
  ],
  // Add more cities for other states
};

const propertyTypes: FilterOption[] = [
  { id: 'apartment', name: 'Apartment' },
  { id: 'house', name: 'House' },
  { id: 'duplex', name: 'Duplex' },
  { id: 'townhouse', name: 'Townhouse' },
  { id: 'land', name: 'Land' },
  { id: 'commercial', name: 'Commercial' },
];

const listingTypes: FilterOption[] = [
  { id: 'for-rent', name: 'For Rent' },
  { id: 'for-sale', name: 'For Sale' },
];

interface ListingsSearchFilterProps {
  onFilterChange: (filters: FilterState) => void;
  className?: string;
}

// Define the type for filter state
interface FilterState {
  query: string;
  state: string;
  city: string;
  propertyType: string;
  listingType: string;
}

const ListingsSearchFilter = ({ onFilterChange, className = '' }: ListingsSearchFilterProps) => {
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    query: '',
    state: '',
    city: '',
    propertyType: '',
    listingType: '',
  });

  // UI state
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [availableCities, setAvailableCities] = useState<FilterOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Update available cities when state changes
  useEffect(() => {
    if (filters.state && citiesByState[filters.state]) {
      setAvailableCities(citiesByState[filters.state]);
    } else {
      setAvailableCities([]);
    }
    // Reset city when state changes
    setFilters(prev => ({ ...prev, city: '' }));
  }, [filters.state]);

  // Apply filters
  const applyFilters = () => {
    setIsSearching(true);
    onFilterChange(filters);
    
    // Simulate search delay
    setTimeout(() => {
      setIsSearching(false);
    }, 800);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      query: '',
      state: '',
      city: '',
      propertyType: '',
      listingType: '',
    });
    onFilterChange({
      query: '',
      state: '',
      city: '',
      propertyType: '',
      listingType: '',
    });
  };

  // Handle filter change
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`bg-white rounded-xl shadow-lg p-4 ${className}`}
    >
      {/* Main search bar */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="grow flex items-center gap-2 p-2 border border-[var(--color-neutral-300)] rounded-lg focus-within:ring-2 focus-within:ring-[var(--color-focus-ring)] focus-within:border-[var(--color-brand-primary)] transition-all">
          <Search className="h-5 w-5 text-[var(--color-neutral-400)]" />
          <input
            type="text"
            placeholder="Search by address, neighborhood, or keyword"
            className="w-full outline-none text-[var(--color-neutral-700)]"
            value={filters.query}
            onChange={(e) => handleFilterChange('query', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          {filters.query && (
            <button
              onClick={() => handleFilterChange('query', '')}
              className="text-[var(--color-neutral-400)] hover:text-[var(--color-neutral-600)]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter toggle button (mobile) */}
        <div className="lg:hidden">
          <Button
            variant="outline"
            className="w-full border-[var(--color-neutral-300)] text-[var(--color-neutral-700)] flex items-center justify-center gap-2"
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
          >
            <Filter className="h-4 w-4" />
            {isFilterExpanded ? 'Hide Filters' : 'Show Filters'}
            <ChevronDown className={`h-4 w-4 transition-transform ${isFilterExpanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Desktop filter controls (always visible on desktop) */}
        <div className="hidden lg:flex items-center gap-3">
          {/* State Dropdown */}
          <select
            className="p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)] transition-all"
            value={filters.state}
            onChange={(e) => handleFilterChange('state', e.target.value)}
          >
            <option value="">All States</option>
            {nigerianStates.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          {/* City Dropdown (only enabled if state is selected) */}
          <select
            className="p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)] transition-all disabled:bg-[var(--color-neutral-100)] disabled:text-[var(--color-neutral-400)]"
            value={filters.city}
            onChange={(e) => handleFilterChange('city', e.target.value)}
            disabled={!filters.state}
          >
            <option value="">All Cities</option>
            {availableCities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>

          {/* Property Type Dropdown */}
          <select
            className="p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)] transition-all"
            value={filters.propertyType}
            onChange={(e) => handleFilterChange('propertyType', e.target.value)}
          >
            <option value="">All Property Types</option>
            {propertyTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>

          {/* Listing Type Dropdown */}
          <select
            className="p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)] transition-all"
            value={filters.listingType}
            onChange={(e) => handleFilterChange('listingType', e.target.value)}
          >
            <option value="">All Listings</option>
            {listingTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>

          {/* Search button */}
          <Button 
            onClick={applyFilters}
            className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white"
            disabled={isSearching}
          >
            {isSearching ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Search
              </>
            )}
          </Button>

          {/* Reset button */}
          {(filters.query || filters.state || filters.city || filters.propertyType || filters.listingType) && (
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="text-[var(--color-neutral-500)] hover:text-[var(--color-neutral-700)]"
            >
              <X className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Mobile filters (expandable) */}
      <AnimatePresence>
        {isFilterExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {/* State Dropdown */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-neutral-700)] mb-1">State</label>
                <select
                  className="w-full p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)]"
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                >
                  <option value="">All States</option>
                  {nigerianStates.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* City Dropdown */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-neutral-700)] mb-1">City</label>
                <select
                  className="w-full p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)] disabled:bg-[var(--color-neutral-100)] disabled:text-[var(--color-neutral-400)]"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  disabled={!filters.state}
                >
                  <option value="">All Cities</option>
                  {availableCities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Property Type Dropdown */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-neutral-700)] mb-1">Property Type</label>
                <select
                  className="w-full p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)]"
                  value={filters.propertyType}
                  onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                >
                  <option value="">All Property Types</option>
                  {propertyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Listing Type Dropdown */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-neutral-700)] mb-1">Listing Type</label>
                <select
                  className="w-full p-2 border border-[var(--color-neutral-300)] rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-[var(--color-brand-primary)] text-[var(--color-neutral-700)] bg-[var(--color-pure-white)]"
                  value={filters.listingType}
                  onChange={(e) => handleFilterChange('listingType', e.target.value)}
                >
                  <option value="">All Listings</option>
                  {listingTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={resetFilters}
                className="text-[var(--color-neutral-500)]"
              >
                <X className="h-4 w-4 mr-2" />
                Reset All
              </Button>

              <Button 
                onClick={applyFilters}
                className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white"
                disabled={isSearching}
              >
                {isSearching ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Apply Filters
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active filters summary (if any filter is applied) */}
      {(filters.state || filters.city || filters.propertyType || filters.listingType) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="mt-3 flex flex-wrap gap-2"
        >
          <span className="text-sm text-[var(--color-neutral-500)]">Active filters:</span>
          
          {filters.state && (
            <div className="inline-flex items-center gap-1 bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)] px-2 py-1 rounded-full text-xs">
              <MapPin className="h-3 w-3" />
              {nigerianStates.find(s => s.id === filters.state)?.name}
              <button
                onClick={() => handleFilterChange('state', '')}
                className="ml-1 text-[var(--color-brand-primary)] hover:text-[var(--color-brand-primary)]/80"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {filters.city && (
            <div className="inline-flex items-center gap-1 bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)] px-2 py-1 rounded-full text-xs">
              <MapPin className="h-3 w-3" />
              {availableCities.find(c => c.id === filters.city)?.name}
              <button
                onClick={() => handleFilterChange('city', '')}
                className="ml-1 text-[var(--color-brand-primary)] hover:text-[var(--color-brand-primary)]/80"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {filters.propertyType && (
            <div className="inline-flex items-center gap-1 bg-[var(--color-cta-success)]/10 text-[var(--color-cta-success)] px-2 py-1 rounded-full text-xs">
              <Building2 className="h-3 w-3" />
              {propertyTypes.find(t => t.id === filters.propertyType)?.name}
              <button
                onClick={() => handleFilterChange('propertyType', '')}
                className="ml-1 text-[var(--color-cta-success)] hover:text-[var(--color-cta-success)]/80"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {filters.listingType && (
            <div className="inline-flex items-center gap-1 bg-[var(--color-cta-accent)]/10 text-[var(--color-cta-accent)] px-2 py-1 rounded-full text-xs">
              <Home className="h-3 w-3" />
              {listingTypes.find(t => t.id === filters.listingType)?.name}
              <button
                onClick={() => handleFilterChange('listingType', '')}
                className="ml-1 text-[var(--color-cta-accent)] hover:text-[var(--color-cta-accent)]/80"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ListingsSearchFilter; 